import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ScheduleConfigurationGap } from '@signalvote/shared';

/**
 * Alert-don't-block banners for filing-schedule configuration gaps
 * (`DeadlineService.GetScheduleConfigurationGapsAsync`, surfaced as
 * `DeadlineListResponse.configurationGaps`).
 *
 * Each gap explains WHY part of the filing calendar isn't generating — election
 * dates or a committee formation date the chosen ruleset needs but the campaign
 * hasn't set, or (VA/ADR-035) a general date whose month has no schedule variant.
 * The server ships treasurer-facing copy in `gap.message`, so this renders that
 * verbatim: no per-type mapping, and a newly-added backend gap type shows up here
 * automatically. Jurisdiction-neutral (federal/AL/VA alike).
 *
 * These are warnings, not blockers — they never gate any action. Renders nothing
 * when there are no gaps.
 */
export function ConfigurationGapAlerts({ gaps }: { gaps: ScheduleConfigurationGap[] }) {
  if (!gaps || gaps.length === 0) return null;

  return (
    <div data-testid="config-gap-alerts" className="flex flex-col gap-2">
      {gaps.map((gap, i) => (
        <div
          key={`${gap.type}-${i}`}
          data-testid="config-gap-alert"
          role="status"
          className={cn(
            'flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50',
            'px-4 py-3 text-[13px] text-amber-900 leading-snug',
          )}
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" aria-hidden />
          <p>{gap.message}</p>
        </div>
      ))}
    </div>
  );
}
